import moment from "moment";
import React from "react";

import { ReferralService, SearchOptions, SearchService } from "@pro/_services";
import { PageInternal } from "@pro/containers/PageInternal";

import { Button } from "@common/components/Atoms/Button";
import Error from "@common/components/Atoms/Error";
import { IconChevronDown } from "@common/components/Atoms/Icons";
import Loading from "@common/components/Atoms/Loading";
import ActivitiesList from "@common/components/Molecules/ActivitiesList";
import Dropdown, { DropdownItem, DropdownMenu } from "@common/components/Molecules/Dropdown";
import { Activity as ActivityModel } from "@common/models";
import { Route, RouteComponentProps } from "react-router";

/*===========================================================================
  Page-level actions for > small screens
/*======================================================================== */

const PageActionsLarge = props => (
    <div className="c-page__actions">
        <Button
            id="view-referral-button"
            onClick={() => props.history.push("/referrals/" + props.referral)}>
            view referral
        </Button>
    </div>
);

/*===========================================================================
  Page-level actions for <= small screens
/*======================================================================== */

const PageActionsSmall = props => (
    <Dropdown right={true}>
        <Button
            id="dropdown-toggle-button"
            square={true}
            className="c-dropdown__toggle"
            onClick={() => {
                return;
            }}>
            <IconChevronDown />
        </Button>
        <DropdownMenu>
            <DropdownItem linkTo={"/referrals/" + props.referral}>view referral</DropdownItem>
        </DropdownMenu>
    </Dropdown>
);

interface ReferralActivitiesProps extends RouteComponentProps<any> { }
interface ReferralActivitiesState {
    search: SearchOptions;
    error: boolean;
    errorMessage: "";
    loading: boolean;
    updating: boolean;
    filtersModalIsOpen: boolean;
    inputStartDate: string;
    inputEndDate: string;
    results?: {
        results: ActivityModel[];
        current_page: number;
        page_size: number;
        total: number;
    };
    jobCandidateId: number;
}

export class ReferralActivities extends React.Component<
    ReferralActivitiesProps,
    ReferralActivitiesState
    > {
    private _abortControllers: { [key: string]: AbortController } = {};
    searchService: SearchService;
    removeHistoryListener: () => void;

    constructor(props) {
        super(props);

        this.emptySearchOptions = this.emptySearchOptions.bind(this);
        this.openModal = this.openModal.bind(this);
        this.closeModal = this.closeModal.bind(this);
        this.fetchActivities = this.fetchActivities.bind(this);
        this.rangeChanged = this.rangeChanged.bind(this);
        this.onPageChange = this.onPageChange.bind(this);
        this.onUrlChange = this.onUrlChange.bind(this);

        this.searchService = new SearchService(
            this.emptySearchOptions,
            this.fetchActivities,
            this.props.history
        );

        this.state = {
            error: false,
            errorMessage: "",
            loading: true,
            updating: false,
            filtersModalIsOpen: false,
            inputStartDate: moment()
                .add(-90, "days")
                .format(this.date_format),
            inputEndDate: moment().format(this.date_format),
            search: this.emptySearchOptions(),
            results: undefined,
            jobCandidateId: this.props.match.params.id,
        };
    }

    date_format = "YYYY-MM-DD";

    emptySearchOptions(): SearchOptions {
        return {
            current_page: 1,
            page_size: 25,
            range_start: moment()
                .add(-90, "days")
                .toISOString(),
            range_end: moment().toISOString(),
            search_term: "",
        };
    }

    componentDidMount() {
        this.searchService.updateFromUrl(this.props.location);
        this.removeHistoryListener = this.props.history.listen(this.onUrlChange);
    }

    componentWillUnmount() {
        if (this.removeHistoryListener) { this.removeHistoryListener(); }
        const abortControllers = this._abortControllers;
        if (Object.keys(abortControllers).length > 0) {
            Object.keys(abortControllers).map(key => abortControllers[key].abort());
        }
    }

    onUrlChange(location: any) {
        this.searchService.updateFromUrl(location);
    }

    onPageChange(pageNumber: number) {
        this.searchService.updateUrl({
            ...this.state.search,
            current_page: pageNumber,
        });
    }

    closeModal(modal: string) {
        const state = {};
        state[modal] = false;
        this.setState(state);
    }

    openModal(modal: string) {
        const state = {};
        state[modal] = true;
        this.setState(state);
    }

    rangeChanged(start: string, end: string) {
        this.closeModal("filtersModalIsOpen");

        const utcStart = moment(start)
            .startOf("day")
            .toISOString();
        const utcEnd = moment(end)
            .endOf("day")
            .toISOString();

        this.setState({ inputStartDate: start, inputEndDate: end }, () =>
            this.searchService.updateUrl({
                ...this.state.search,
                range_start: utcStart,
                range_end: utcEnd,
            })
        );
    }

    fetchActivities(options: SearchOptions) {
        this.setState({ updating: true }, () => {
            // Check to see if there already was an abortController, and if so, call abort on it
            if (this._abortControllers.getActivities) {
                this._abortControllers.getActivities.abort();
            }
            this._abortControllers.getActivities = new AbortController();
            const getActivitiesSignal = this._abortControllers.getActivities.signal;

            ReferralService.getActivities(options, this.state.jobCandidateId, getActivitiesSignal)
                .then(result => {
                    const defaultSearch = this.emptySearchOptions();
                    const search = {
                        current_page: result.current_page || defaultSearch.current_page,
                        page_size: result.page_size || defaultSearch.page_size,
                        range_start: result.range_start || defaultSearch.range_start,
                        range_end: result.range_end || defaultSearch.range_end,
                    } as SearchOptions;

                    this.setState(
                        prevState => ({
                            error: false,
                            loading: false,
                            updating: false,
                            search,
                            results: result,
                        }),
                        () => {
                            window.scrollTo(0, 0);
                        }
                    );
                })
                .catch(err => {
                    console.log(err);
                    if (err.type === "abort" || err.name === "AbortError") {
                        this.setState({ updating: false, loading: false });
                        return;
                    }

                    this.setState({
                        error: true,
                        errorMessage: err.message,
                        updating: false,
                        loading: false,
                    });
                });
        });
    }

    render() {
        const { loading, error, updating } = this.state;
        if (error) {
            return (
                <PageInternal title="activity">
                    <Error show={this.state.error} description={this.state.errorMessage} />
                </PageInternal>
            );
        } else if (loading) {
            return (
                <PageInternal title="activity">
                    <Loading />
                </PageInternal>
            );
        }

        return (
            <PageInternal
                updating={updating}
                title="activity"
                actionsLarge={
                    <PageActionsLarge
                        referral={this.state.jobCandidateId}
                        history={this.props.history}
                    />
                }
                actionsSmall={<PageActionsSmall referral={this.state.jobCandidateId} />}>
                <ActivitiesList
                    rangeStart={this.state.inputStartDate}
                    rangeEnd={this.state.inputEndDate}
                    data={this.state.results}
                    isLoading={this.state.loading}
                    onRangeChange={this.rangeChanged}
                    onPageChange={this.onPageChange}
                    filtersModalIsOpen={this.state.filtersModalIsOpen}
                    openModal={this.openModal}
                    closeModal={this.closeModal}
                />
            </PageInternal>
        );
    }
}
